import { differenceInDays, isAfter, startOfDay, subDays } from 'date-fns'
import { getUsageTypeLabel } from '@/routes/usage/constants'
import type { UsageGroup, UsageRow } from '@/routes/usage/types'

type DailyReport = {
  days: {
    date: Date | string | number
    services: {
      idService?: string | null
      serviceName?: string | null
      totalGross: number
      usageByType: {
        type: string
        totalUsage: number
        unitPrice: number
        net: number
        gross: number
        tax: number
      }[]
    }[]
  }[]
  periodEnd: Date | string
  totalGross: number
}

type ProjectionResult = {
  projectedTotal: number
  projectedDailyAmount: number
}

/**
 * Returns the last day with fully reported usage data.
 * Usage reports have a ~6 hour delay, so:
 * - Before 6am UTC: yesterday's data is still incomplete → use day before yesterday
 * - After 6am UTC: yesterday's data is complete → use yesterday
 * Today's data is always excluded (always incomplete).
 */
export const getLastCompletedDay = (): Date => {
  const now = new Date()
  const utcHour = now.getUTCHours()
  return utcHour >= 6
    ? startOfDay(subDays(now, 1))
    : startOfDay(subDays(now, 2))
}

export const computeProjection = ({
  days,
  periodEnd,
}: {
  days: DailyReport['days']
  periodEnd: Date | string
}): ProjectionResult => {
  const endDate = new Date(periodEnd)
  const lastCompleted = getLastCompletedDay()

  if (days.length === 0) {
    return { projectedTotal: 0, projectedDailyAmount: 0 }
  }

  // Only sum days that are fully reported
  const completedDays = days.filter((day) => {
    const dayDate = startOfDay(new Date(day.date))
    return !isAfter(dayDate, lastCompleted)
  })

  if (completedDays.length === 0) {
    return { projectedTotal: 0, projectedDailyAmount: 0 }
  }

  // Exclude the first day from the average — it's usually a partial day
  // (billing cycle may start mid-day, so it doesn't represent a full 24h)
  const firstDayTotal =
    completedDays.length > 1
      ? completedDays[0].services.reduce(
          (sSum, svc) =>
            sSum + svc.usageByType.reduce((tSum, u) => tSum + u.gross, 0),
          0
        )
      : 0

  const totalCompleted = completedDays.reduce((sum, day) => {
    const dayTotal = day.services.reduce(
      (sSum, svc) =>
        sSum + svc.usageByType.reduce((tSum, u) => tSum + u.gross, 0),
      0
    )
    return sum + dayTotal
  }, 0)

  const avgDaily =
    completedDays.length > 1
      ? (totalCompleted - firstDayTotal) / (completedDays.length - 1)
      : totalCompleted / completedDays.length

  // Remaining days = from day after lastCompleted to periodEnd
  const remainingDays = Math.max(0, differenceInDays(endDate, lastCompleted))
  const projectedTotal = totalCompleted + remainingDays * avgDaily

  return { projectedTotal, projectedDailyAmount: avgDaily }
}

export const computeComparison = ({
  currentTotal,
  previousTotal,
}: {
  currentTotal: number
  previousTotal: number
}): { percentageChange: number; direction: 'up' | 'down' | 'flat' } => {
  if (previousTotal === 0) {
    if (currentTotal === 0) {
      return { percentageChange: 0, direction: 'flat' }
    }
    return { percentageChange: 100, direction: 'up' }
  }

  const change = ((currentTotal - previousTotal) / previousTotal) * 100

  if (Math.abs(change) < 0.01) {
    return { percentageChange: 0, direction: 'flat' }
  }

  return {
    percentageChange: Math.abs(change),
    direction: change > 0 ? 'up' : 'down',
  }
}

export const formatMoney = (amount: number, symbol = '$'): string => {
  return `${symbol}${amount.toFixed(2)}`
}

type FilterArgs = {
  report: DailyReport | null | undefined
  selectedServices: string[]
  selectedTypes: string[]
  serviceToProject: Map<string, string>
  selectedProjects: string[]
}

export const filterDailyReport = ({
  report,
  selectedServices,
  selectedTypes,
  serviceToProject,
  selectedProjects,
}: FilterArgs): DailyReport | null => {
  if (!report) {
    return null
  }

  const projectServiceIds =
    selectedProjects.length > 0
      ? new Set(
          Array.from(serviceToProject.entries())
            .filter(([, idProject]) => selectedProjects.includes(idProject))
            .map(([idService]) => idService)
        )
      : null

  const serviceFilter =
    selectedServices.length > 0 ? new Set(selectedServices) : null

  const filteredDays = report.days.map((day) => {
    const filteredServices = day.services
      .filter((svc) => {
        if (projectServiceIds && !projectServiceIds.has(svc.idService ?? '')) {
          return false
        }
        if (serviceFilter && !serviceFilter.has(svc.idService ?? '')) {
          return false
        }
        return true
      })
      .map((svc) => {
        if (selectedTypes.length === 0) {
          return svc
        }
        const typeSet = new Set(selectedTypes)
        const filteredUsage = svc.usageByType.filter((u) => typeSet.has(u.type))
        return {
          ...svc,
          usageByType: filteredUsage,
          totalGross: filteredUsage.reduce((s, u) => s + u.gross, 0),
        }
      })

    return {
      ...day,
      services: filteredServices,
    }
  })

  const totalGross = filteredDays.reduce(
    (sum, day) =>
      sum + day.services.reduce((sSum, svc) => sSum + svc.totalGross, 0),
    0
  )

  return {
    ...report,
    days: filteredDays,
    totalGross,
  }
}

export const aggregateUsageRows = ({
  report,
}: {
  report: DailyReport | null | undefined
}): UsageRow[] => {
  if (!report) {
    return []
  }

  const rowMap = new Map<string, UsageRow>()

  for (const day of report.days) {
    for (const svc of day.services) {
      for (const usage of svc.usageByType) {
        const key = `${svc.idService ?? 'unknown'}::${usage.type}`
        const existing = rowMap.get(key)

        if (existing) {
          existing.totalUsage += usage.totalUsage
          existing.net += usage.net
          existing.gross += usage.gross
          existing.tax += usage.tax
        } else {
          rowMap.set(key, {
            idService: svc.idService ?? 'unknown',
            serviceName: svc.serviceName ?? svc.idService ?? 'Unknown service',
            type: usage.type,
            totalUsage: usage.totalUsage,
            unitPrice: usage.unitPrice,
            net: usage.net,
            gross: usage.gross,
            tax: usage.tax,
          })
        }
      }
    }
  }

  return Array.from(rowMap.values()).sort((a, b) => b.gross - a.gross)
}

export const groupUsageRows = (rows: UsageRow[]): UsageGroup[] => {
  const groupMap = new Map<string, UsageGroup>()

  for (const row of rows) {
    const existing = groupMap.get(row.type)

    if (existing) {
      existing.net += row.net
      existing.gross += row.gross
      existing.tax += row.tax
      existing.rows.push(row)
    } else {
      groupMap.set(row.type, {
        type: row.type,
        label: getUsageTypeLabel(row.type),
        net: row.net,
        gross: row.gross,
        tax: row.tax,
        rows: [row],
      })
    }
  }

  const groups = Array.from(groupMap.values())

  // Sort groups by gross descending, sort rows within each group too
  for (const group of groups) {
    group.rows.sort((a, b) => b.gross - a.gross)
  }

  return groups.sort((a, b) => b.gross - a.gross)
}

const downloadFile = ({
  content,
  filename,
  type,
}: {
  content: string
  filename: string
  type: string
}): void => {
  const blob = new Blob([content], { type })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}

export const exportUsageAsCsv = ({
  groups,
  filename,
}: {
  groups: UsageGroup[]
  filename: string
}): void => {
  const headers = ['Service type', 'Service', 'Net', 'Gross', 'Tax']

  const csvRows: string[] = []

  for (const group of groups) {
    if (group.gross <= 0) {
      continue
    }
    for (const row of group.rows) {
      if (row.gross <= 0) {
        continue
      }
      csvRows.push(
        [
          `"${group.label}"`,
          `"${row.serviceName}"`,
          row.net.toFixed(2),
          row.gross.toFixed(2),
          row.tax.toFixed(2),
        ].join(',')
      )
    }
  }

  const csv = [headers.join(','), ...csvRows].join('\n')
  downloadFile({ content: csv, filename, type: 'text/csv;charset=utf-8;' })
}

export const exportUsageAsJson = ({
  groups,
  filename,
}: {
  groups: UsageGroup[]
  filename: string
}): void => {
  const data = groups
    .filter((group) => group.gross > 0)
    .map((group) => ({
      serviceType: group.label,
      net: Number(group.net.toFixed(2)),
      gross: Number(group.gross.toFixed(2)),
      tax: Number(group.tax.toFixed(2)),
      services: group.rows
        .filter((row) => row.gross > 0)
        .map((row) => ({
          serviceName: row.serviceName,
          net: Number(row.net.toFixed(2)),
          gross: Number(row.gross.toFixed(2)),
          tax: Number(row.tax.toFixed(2)),
        })),
    }))

  const json = JSON.stringify(data, null, 2)
  downloadFile({ content: json, filename, type: 'application/json' })
}
